/**
 * Collection page state + scroll restoration
 * Saves filters/sort URL, scroll position, load-more pages, and clicked product.
 * Restores on Back / Safari bfcache so shoppers return where they left off.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'ymi_collection_state';
  var MAX_AGE_MS = 1000 * 60 * 60 * 2; // 2 hours
  var SAVE_SCROLL_MS = 200;
  var restoring = false;
  var pagesLoaded = 1;
  var scrollTimer = null;
  var lastPathKey = '';

  function isCollectionPage() {
    return document.body && document.body.classList.contains('template-collection');
  }

  function isProductPage() {
    return document.body && document.body.classList.contains('template-product');
  }

  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  function qsa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function pathKey(url) {
    try {
      var u = typeof url === 'string' ? new URL(url, window.location.origin) : new URL(window.location.href);
      // Normalize: drop hash; keep search (filters/sort)
      return u.pathname + u.search;
    } catch (e) {
      return window.location.pathname + window.location.search;
    }
  }

  function getPageFromHref(href) {
    if (!href) return null;
    try {
      var u = new URL(href, window.location.origin);
      var p = u.searchParams.get('page');
      return p ? parseInt(p, 10) : 1;
    } catch (e) {
      var m = String(href).match(/[?&]page=(\d+)/);
      return m ? parseInt(m[1], 10) : 1;
    }
  }

  function inferPagesLoaded() {
    var btn = qs('[data-load-more]');
    if (!btn) return pagesLoaded;
    var href = btn.getAttribute('href');
    if (!href || btn.classList.contains('is-hidden')) return pagesLoaded;
    var next = getPageFromHref(href);
    if (next && next > 1) return Math.max(pagesLoaded, next - 1);
    return pagesLoaded;
  }

  function readState() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || !data.path) return null;
      if (data.ts && Date.now() - data.ts > MAX_AGE_MS) {
        sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return data;
    } catch (e) {
      return null;
    }
  }

  function writeState(partial) {
    try {
      var prev = readState() || {};
      var next = {
        path: partial.path != null ? partial.path : prev.path || pathKey(),
        scrollY: partial.scrollY != null ? partial.scrollY : (prev.scrollY || 0),
        pagesLoaded: partial.pagesLoaded != null ? partial.pagesLoaded : (prev.pagesLoaded || pagesLoaded || 1),
        productHandle: partial.productHandle != null ? partial.productHandle : prev.productHandle || '',
        productId: partial.productId != null ? partial.productId : prev.productId || '',
        nextHref: partial.nextHref != null ? partial.nextHref : prev.nextHref || '',
        ts: Date.now()
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    } catch (e) {
      return null;
    }
  }

  function saveSnapshot(extra) {
    if (!isCollectionPage() || restoring) return;
    var btn = qs('[data-load-more]');
    var nextHref = btn && !btn.classList.contains('is-hidden') ? (btn.getAttribute('href') || '') : '';
    pagesLoaded = Math.max(pagesLoaded, inferPagesLoaded());
    writeState(Object.assign({
      path: pathKey(),
      scrollY: window.scrollY || window.pageYOffset || 0,
      pagesLoaded: pagesLoaded,
      nextHref: nextHref
    }, extra || {}));
  }

  function resetPagesCounter() {
    pagesLoaded = 1;
    lastPathKey = pathKey();
  }

  function getGrid() {
    return qs('.js-infinite-wrapper[data-load-more--grid]') || qs('.js-infinite-wrapper');
  }

  function fetchPageHtml(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('load failed');
      return r.text();
    });
  }

  function appendPageFromHtml(html) {
    var grid = getGrid();
    if (!grid) return false;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var items = doc.querySelectorAll('[data-load-more--grid-item]');
    var nextBtn = doc.querySelector('[data-load-more]');
    var loadMoreWrap = qs('.js-load-more', grid) || qs('.js-load-more');
    var loadMoreIcon = qs('.load-more__icon');

    items.forEach(function (el) {
      grid.appendChild(document.importNode(el, true));
    });

    var liveBtn = qs('[data-load-more]');
    if (nextBtn && nextBtn.getAttribute('href')) {
      if (liveBtn) {
        liveBtn.setAttribute('href', nextBtn.getAttribute('href'));
        liveBtn.classList.remove('is-hidden');
      }
    } else if (liveBtn) {
      liveBtn.removeAttribute('href');
      liveBtn.classList.add('is-hidden');
    }

    if (loadMoreWrap && loadMoreWrap.parentNode === grid) {
      grid.appendChild(loadMoreWrap);
    }
    if (loadMoreIcon && loadMoreIcon.parentNode === grid) {
      grid.appendChild(loadMoreIcon);
    }

    if (window.theme && typeof theme.reinitProductGridItem1 === 'function') {
      theme.reinitProductGridItem1();
    } else if (window.theme && typeof theme.reinitProductGridItem === 'function') {
      theme.reinitProductGridItem();
    }
    document.dispatchEvent(new CustomEvent('collection:reloaded'));
    return items.length > 0;
  }

  function loadPagesUntil(targetPages) {
    targetPages = Math.max(1, parseInt(targetPages, 10) || 1);
    if (targetPages <= 1) return Promise.resolve();

    var chain = Promise.resolve();
    var loaded = 1;

    function step() {
      if (loaded >= targetPages) return Promise.resolve();
      var btn = qs('[data-load-more]');
      var href = btn && !btn.classList.contains('is-hidden') ? btn.getAttribute('href') : null;
      if (!href) return Promise.resolve();

      return fetchPageHtml(href).then(function (html) {
        appendPageFromHtml(html);
        loaded += 1;
        pagesLoaded = Math.max(pagesLoaded, loaded);
        return step();
      });
    }

    return chain.then(step);
  }

  function scrollToSaved(state) {
    var y = state && typeof state.scrollY === 'number' ? state.scrollY : 0;
    var handle = state && state.productHandle;

    function apply() {
      if (handle) {
        var card = null;
        qsa('.grid-product[data-product-handle]').forEach(function (el) {
          if (!card && el.getAttribute('data-product-handle') === handle) card = el;
        });
        if (card) {
          // Prefer exact saved scroll; fall back to bringing card into view
          window.scrollTo(0, y);
          var rect = card.getBoundingClientRect();
          if (rect.top < 0 || rect.bottom > window.innerHeight) {
            var top = rect.top + (window.scrollY || 0) - Math.min(120, window.innerHeight * 0.15);
            window.scrollTo(0, Math.max(0, top));
          }
          card.classList.add('ymi-restore-highlight');
          setTimeout(function () {
            card.classList.remove('ymi-restore-highlight');
          }, 1600);
          return;
        }
      }
      window.scrollTo(0, y);
    }

    // Layout may shift after images / sticky bars
    requestAnimationFrame(function () {
      apply();
      setTimeout(apply, 50);
      setTimeout(apply, 250);
    });
  }

  function sameCollectionState(state) {
    if (!state || !state.path) return false;
    try {
      var a = new URL(state.path, window.location.origin);
      var b = new URL(window.location.href);
      if (a.pathname !== b.pathname) return false;

      function norm(search) {
        var params = new URLSearchParams(search);
        // Ignore pagination page when comparing filter/sort identity
        params.delete('page');
        var keys = [];
        params.forEach(function (_v, k) { keys.push(k); });
        keys.sort();
        return keys.map(function (k) {
          return k + '=' + params.getAll(k).slice().sort().join(',');
        }).join('&');
      }

      return norm(a.search) === norm(b.search);
    } catch (e) {
      return state.path.split('?')[0] === window.location.pathname;
    }
  }

  function isBackForwardNavigation() {
    try {
      var nav = performance.getEntriesByType && performance.getEntriesByType('navigation');
      if (nav && nav[0] && nav[0].type === 'back_forward') return true;
    } catch (e) { /* ignore */ }
    try {
      if (performance.navigation && performance.navigation.type === 2) return true;
    } catch (e2) { /* ignore */ }
    return false;
  }

  function restoreFromState(state, fromBfcache) {
    if (!state || !isCollectionPage()) return Promise.resolve(false);
    if (!sameCollectionState(state)) return Promise.resolve(false);

    restoring = true;
    pagesLoaded = Math.max(1, parseInt(state.pagesLoaded, 10) || 1);

    var done = Promise.resolve();
    if (!fromBfcache && pagesLoaded > 1) {
      document.documentElement.classList.add('ymi-collection-restoring');
      done = loadPagesUntil(pagesLoaded);
    }

    return done
      .catch(function () { /* partial restore still useful */ })
      .then(function () {
        scrollToSaved(state);
        document.documentElement.classList.remove('ymi-collection-restoring');
        restoring = false;
        saveSnapshot({
          productHandle: state.productHandle || '',
          productId: state.productId || '',
          pagesLoaded: pagesLoaded
        });
        return true;
      });
  }

  function onProductLinkClick(e) {
    if (!isCollectionPage()) return;
    var link = e.target.closest && e.target.closest('a.grid-product__link');
    if (!link) return;

    // Never open collection product cards in a new tab from our handlers
    // (middle-click / modified clicks still allowed by browser)
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) {
      saveSnapshot();
      return;
    }

    var card = link.closest('.grid-product');
    saveSnapshot({
      productHandle: card ? (card.getAttribute('data-product-handle') || '') : '',
      productId: card ? (card.getAttribute('data-product-id') || '') : '',
      pagesLoaded: Math.max(pagesLoaded, inferPagesLoaded()),
      scrollY: window.scrollY || window.pageYOffset || 0
    });
  }

  function stripBlankTargets() {
    if (!isCollectionPage()) return;
    qsa('a.grid-product__link[target="_blank"]').forEach(function (a) {
      a.removeAttribute('target');
      var rel = (a.getAttribute('rel') || '').replace(/\bnoopener\b/g, '').replace(/\bnoreferrer\b/g, '').trim();
      if (rel) a.setAttribute('rel', rel);
      else a.removeAttribute('rel');
    });
  }

  function bindCollection() {
    if (!isCollectionPage()) return;

    try {
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
      }
    } catch (e) { /* ignore */ }

    lastPathKey = pathKey();
    pagesLoaded = Math.max(1, inferPagesLoaded());

    document.addEventListener('click', onProductLinkClick, true);

    window.addEventListener('scroll', function () {
      if (restoring) return;
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(function () {
        saveSnapshot();
      }, SAVE_SCROLL_MS);
    }, { passive: true });

    window.addEventListener('pagehide', function () {
      saveSnapshot();
    });

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') saveSnapshot();
    });

    // Load-more / infinite scroll appended more pages
    document.addEventListener('collection:page-loaded', function (evt) {
      var p = evt && evt.detail && evt.detail.pagesLoaded;
      if (p) pagesLoaded = Math.max(pagesLoaded, parseInt(p, 10) || pagesLoaded);
      else pagesLoaded = Math.max(pagesLoaded, inferPagesLoaded(), pagesLoaded + 1);
      saveSnapshot({ pagesLoaded: pagesLoaded });
    });

    // Filters AJAX replace grid → reset page count; keep path updated
    document.addEventListener('collection:reloaded', function () {
      if (restoring) return;
      var now = pathKey();
      if (now !== lastPathKey) {
        resetPagesCounter();
        lastPathKey = now;
      } else {
        pagesLoaded = Math.max(pagesLoaded, inferPagesLoaded());
      }
      stripBlankTargets();
      saveSnapshot({ pagesLoaded: pagesLoaded });
    });

    // Path change via filter pushState
    var _push = history.pushState;
    var _replace = history.replaceState;
    if (_push) {
      history.pushState = function () {
        var ret = _push.apply(this, arguments);
        if (isCollectionPage()) {
          var now = pathKey();
          if (now !== lastPathKey) {
            resetPagesCounter();
            lastPathKey = now;
          }
          saveSnapshot({ path: now, pagesLoaded: pagesLoaded, scrollY: window.scrollY || 0 });
        }
        return ret;
      };
    }
    if (_replace) {
      history.replaceState = function () {
        var ret = _replace.apply(this, arguments);
        if (isCollectionPage()) {
          lastPathKey = pathKey();
          saveSnapshot({ path: lastPathKey });
        }
        return ret;
      };
    }

    stripBlankTargets();

    // Restore only on Back/Forward (not normal landing/refresh)
    var state = readState();
    if (
      state &&
      sameCollectionState(state) &&
      isBackForwardNavigation() &&
      (state.pagesLoaded > 1 || state.scrollY > 0 || state.productHandle)
    ) {
      setTimeout(function () {
        restoreFromState(state, false);
      }, 50);
    } else {
      saveSnapshot({ pagesLoaded: Math.max(1, inferPagesLoaded()) });
    }
  }

  function bindPageshow() {
    window.addEventListener('pageshow', function (evt) {
      if (!isCollectionPage()) return;
      var state = readState();
      if (!state) return;

      if (evt.persisted) {
        // Safari bfcache: DOM (incl. load-more items) intact — restore scroll only
        restoring = true;
        scrollToSaved(state);
        restoring = false;
        return;
      }
      // Non-persisted back navigation may have already started restore in bindCollection
    });
  }

  function bindBackToResults() {
    if (!isProductPage()) return;
    var state = readState();
    if (!state || !state.path) return;

    var links = qsa('.js-back-to-results, a.return-link');
    if (!links.length) {
      var host = qs('.product-section') || qs('main') || document.body;
      if (!host) return;
      var wrap = document.createElement('div');
      wrap.className = 'ymi-back-to-results-wrap ymi-back-to-results-wrap--top page-width';
      wrap.innerHTML =
        '<a class="ymi-back-to-results-link js-back-to-results" href="' + state.path.replace(/"/g, '') + '">' +
        '<span aria-hidden="true">←</span> <span class="ymi-back-to-results__label">Back to results</span></a>';
      host.insertBefore(wrap, host.firstChild);
      links = qsa('.js-back-to-results', wrap);
    }

    links.forEach(function (a) {
      a.setAttribute('href', state.path);
      a.classList.add('ymi-back-to-results');
      var label = a.querySelector('.ymi-back-to-results__label');
      if (label) label.textContent = 'Back to results';
      a.addEventListener('click', function () {
        state.ts = Date.now();
        try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (e) { /* ignore */ }
      });
    });
  }

  function bindQuickView() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-ymi-quick-view]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      var productId = btn.getAttribute('data-product-id');
      var handle = btn.getAttribute('data-product-handle');
      var variantId = btn.getAttribute('data-variant-id') || '';
      if (!productId || !handle) return;
      if (typeof window.openGlobalQuickView === 'function') {
        window.openGlobalQuickView(productId, handle, variantId);
      }
    }, true);
  }

  function init() {
    if (window.__ymiCollectionStateBound) return;
    window.__ymiCollectionStateBound = true;
    bindPageshow();
    bindQuickView();
    if (isCollectionPage()) bindCollection();
    if (isProductPage()) bindBackToResults();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();